import React, { useMemo, useState } from 'react';
import { Check, Copy, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const GUIDE_IMAGE_DIMENSIONS = {
  '/images/guides/console/request-log.png': [1440, 1100],
  '/images/guides/console/token-create.png': [600, 2000],
  '/images/guides/console/console-overview.png': [1440, 1100],
};

function textFromChildren(children) {
  return React.Children.toArray(children).map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    return child?.props ? textFromChildren(child.props.children) : '';
  }).join('');
}

export function headingId(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[`'"“”‘’]/g, '')
    .replace(/[^\p{Letter}\p{Number}]+/gu, '-')
    .replace(/^-|-$/g, '') || 'section';
}

export function extractHeadings(markdown) {
  return String(markdown || '').split('\n').flatMap((line) => {
    const match = line.match(/^(##|###)\s+(.+?)\s*#*$/);
    if (!match) return [];
    const title = match[2].replace(/\[([^\]]+)\]\([^)]*\)/g, '$1').replace(/[`*_]/g, '');
    return [{ level: match[1].length, title, id: headingId(title) }];
  });
}

function MarkdownCodeBlock({ children }) {
  const [copied, setCopied] = useState(false);
  const code = textFromChildren(children).replace(/\n$/, '');
  const element = React.Children.toArray(children)[0];
  const language = String(element?.props?.className || '').replace(/^language-/, '') || 'text';
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return <div className="markdown-code">
    <header><span>{language}</span><button type="button" onClick={copy}>{copied ? <Check size={14} /> : <Copy size={14} />}{copied ? '已复制' : '复制'}</button></header>
    <pre>{children}</pre>
  </div>;
}

export function Markdown({ children }) {
  return <div className="markdown"><ReactMarkdown remarkPlugins={[remarkGfm]} components={{
    h2: ({ children: label }) => <h2 id={headingId(textFromChildren(label))}>{label}</h2>,
    h3: ({ children: label }) => <h3 id={headingId(textFromChildren(label))}>{label}</h3>,
    a: ({ href, children: label }) => {
      const external = href?.startsWith('http');
      return <a href={href} target={external ? '_blank' : undefined} rel={external ? 'noreferrer' : undefined}>{label}{external && <ExternalLink size={12} aria-hidden="true" />}</a>;
    },
    img: ({ src, alt = '', title = '' }) => {
      const dimensions = GUIDE_IMAGE_DIMENSIONS[src];
      const orientation = dimensions?.[1] > dimensions?.[0] ? 'is-portrait' : 'is-landscape';
      return <figure className={`article-figure ${orientation}`}>
        <a href={src} target="_blank" rel="noreferrer" aria-label={`${alt || '查看图片'}（打开原图）`}>
          <img src={src} alt={alt} loading="lazy" decoding="async" width={dimensions?.[0]} height={dimensions?.[1]} />
        </a>
        {title && <figcaption>{title}</figcaption>}
      </figure>;
    },
    pre: ({ children: code }) => <MarkdownCodeBlock>{code}</MarkdownCodeBlock>,
    code: ({ className, children: code }) => className ? <code className={className}>{code}</code> : <code>{code}</code>,
  }}>{children || ''}</ReactMarkdown></div>;
}

export function ArticleTools({ source }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(source || '');
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1500);
  };
  return <button className="article-copy" onClick={copy}>{copied ? <Check size={15} /> : <Copy size={15} />} {copied ? '已复制' : '复制页面'}</button>;
}

export function ArticleToc({ markdown }) {
  const headings = useMemo(() => extractHeadings(markdown), [markdown]);
  if (!headings.length) return null;
  return <aside className="article-toc" aria-label="本页目录"><span>本页内容</span>{headings.map((heading) => <a className={heading.level === 3 ? 'depth-3' : ''} key={`${heading.id}-${heading.title}`} href={`#${heading.id}`}>{heading.title}</a>)}</aside>;
}
